<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ContactDetailsItem from './ContactDetailsItem.vue';

const props = defineProps({
  conversationAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const source = computed(() => props.conversationAttributes.source || '');
const medium = computed(() => props.conversationAttributes.medium || '');
const campaign = computed(() => props.conversationAttributes.campaign || '');
const landingPageUrl = computed(
  () => props.conversationAttributes.landing_page_url || ''
);

const capitalize = value =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

// Human-readable acquisition channel derived from source/medium.
const channel = computed(() => {
  if (!source.value && !medium.value) return '';
  const m = medium.value.toLowerCase();
  const s = source.value.toLowerCase();
  if (['cpc', 'ppc', 'paid', 'paid_social', 'paidsocial'].includes(m)) {
    if (s.includes('google')) return t('CONTACT_PANEL.CHANNELS.GOOGLE_ADS');
    if (s.includes('facebook') || s.includes('meta') || s.includes('instagram'))
      return t('CONTACT_PANEL.CHANNELS.PAID_SOCIAL');
    return t('CONTACT_PANEL.CHANNELS.PAID');
  }
  if (m === 'organic')
    return `${capitalize(source.value)} ${t('CONTACT_PANEL.CHANNELS.ORGANIC')}`;
  if (m === 'referral') return t('CONTACT_PANEL.CHANNELS.REFERRAL');
  if (m === 'email') return t('CONTACT_PANEL.CHANNELS.EMAIL');
  if (s === 'direct' || m === 'none') return t('CONTACT_PANEL.CHANNELS.DIRECT');
  return `${capitalize(source.value)} / ${medium.value}`;
});

const rows = computed(() =>
  [
    { key: 'source', title: 'CONTACT_PANEL.SOURCE', value: source.value },
    { key: 'medium', title: 'CONTACT_PANEL.MEDIUM', value: medium.value },
    { key: 'campaign', title: 'CONTACT_PANEL.CAMPAIGN', value: campaign.value },
  ].filter(row => row.value)
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div v-if="channel">
      <span
        class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-n-alpha-2 text-n-slate-12"
      >
        {{ channel }}
      </span>
    </div>
    <ContactDetailsItem
      v-for="row in rows"
      :key="row.key"
      compact
      :title="$t(row.title)"
      :value="row.value"
    />
    <ContactDetailsItem
      v-if="landingPageUrl"
      compact
      :title="$t('CONTACT_PANEL.LANDING_PAGE')"
      :value="landingPageUrl"
    >
      <a
        :href="landingPageUrl"
        rel="noopener noreferrer nofollow"
        target="_blank"
        class="text-n-brand break-all"
      >
        {{ landingPageUrl }}
      </a>
    </ContactDetailsItem>
  </div>
</template>
